import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { tokens } from '../../theme/tokens';

interface UsageBarProps {
  label: React.ReactNode;
  used: number;
  /** `null` or `undefined` means unlimited. */
  limit?: number | null;
  unit?: string;
  /** Fractions of the limit at which the bar turns caution, then problem. */
  warnAt?: number;
  dangerAt?: number;
  format?: (value: number) => string;
}

/**
 * Quota bar for billing and plan pages. Two pages carried their own copies
 * with different thresholds, radii and a gradient each.
 */
export const UsageBar: React.FC<UsageBarProps> = ({
  label,
  used,
  limit,
  unit,
  warnAt = 0.7,
  dangerAt = 0.85,
  format = (value) => value.toLocaleString(),
}) => {
  // Only null/undefined are unlimited. A limit of 0 is a real hard cap
  // (e.g. free-plan maxIntegrations) and must not render as "(unlimited)".
  const unlimited = limit === null || limit === undefined;
  const fraction = unlimited ? 0 : Math.min(used / Math.max(limit, 1), 1);
  const tone = fraction >= dangerAt ? 'problem' : fraction >= warnAt ? 'caution' : 'info';
  const bar = tokens.signal[tone];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {format(used)}
          {unlimited ? '' : ` / ${format(limit)}`}
          {unit ? ` ${unit}` : ''}
          {unlimited ? ' (unlimited)' : ''}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={fraction * 100}
        aria-label={typeof label === 'string' ? `${label} usage` : undefined}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: tokens.surface.tint,
          '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: bar.solid },
        }}
      />
    </Box>
  );
};
